import { useState, useEffect } from 'react';
import { Table, Button, Space, Tag, Modal, message, Popconfirm } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, BarChartOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import EventForm from './EventForm';

interface Event {
  id: string;
  name: string;
  slug: string;
  startDate: string;
  endDate: string;
  location: string;
  eventType: string;
  isActive: boolean;
  emailTemplateId?: string;
  emailTemplate?: {
    id: string;
    name: string;
  };
  _count: {
    feedbacks: number;
    simpleFeedbacks: number;
  };
}

export default function EventsList() {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingEvent, setEditingEvent] = useState<Event | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchEvents();
  }, []);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const res = await fetch('/api/v1/feedback/events', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      const data = await res.json();
      setEvents(data);
    } catch (error) {
      message.error('Failed to fetch events');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await fetch(`/api/v1/feedback/events/${id}`, {
        method: 'DELETE',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });
      message.success('Event deleted successfully');
      fetchEvents();
    } catch (error) {
      message.error('Failed to delete event');
    }
  };

  const handleEdit = (event: Event) => {
    setEditingEvent(event);
    setIsModalVisible(true);
  };

  const handleCreate = () => {
    setEditingEvent(null);
    setIsModalVisible(true);
  };

  const handleModalClose = () => {
    setIsModalVisible(false);
    setEditingEvent(null);
    fetchEvents();
  };

  const columns = [
    {
      title: 'Event Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: Event) => (
        <div>
          <div style={{ fontWeight: 'bold' }}>{text}</div>
          <div style={{ fontSize: 12, color: '#888' }}>{record.slug}</div>
        </div>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'eventType',
      key: 'eventType',
      render: (type: string) => (
        <Tag color={type === 'conference' ? 'blue' : 'green'}>
          {type?.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: 'Location',
      dataIndex: 'location',
      key: 'location',
    },
    {
      title: 'Date Range',
      key: 'dates',
      render: (_: any, record: Event) => (
        <div>
          {new Date(record.startDate).toLocaleDateString()} - {new Date(record.endDate).toLocaleDateString()}
        </div>
      ),
    },
    {
      title: 'Responses',
      key: 'responses',
      render: (_: any, record: Event) => (
        <div>
          <Tag color="purple">
            {record._count.feedbacks + record._count.simpleFeedbacks} total
          </Tag>
        </div>
      ),
    },
    {
      title: 'Email Template',
      key: 'emailTemplate',
      render: (_: any, record: Event) => (
        <div>
          {record.emailTemplate ? (
            <Tag color="blue">{record.emailTemplate.name}</Tag>
          ) : (
            <Tag color="default">None</Tag>
          )}
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'isActive',
      key: 'isActive',
      render: (isActive: boolean) => (
        <Tag color={isActive ? 'green' : 'red'}>
          {isActive ? 'Active' : 'Inactive'}
        </Tag>
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: any, record: Event) => (
        <Space>
          <Button
            type="primary"
            onClick={() => navigate(`/feedback/events/${record.id}`)}
          >
            View Details
          </Button>
          <Button
            type="link"
            icon={<BarChartOutlined />}
            onClick={() => navigate(`/feedback/events/${record.id}/analytics`)}
          >
            Analytics
          </Button>
          <Button
            type="link"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          >
            Edit
          </Button>
          <Popconfirm
            title="Delete this event?"
            description="This will not delete feedback data, only the event."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button type="link" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between' }}>
        <h1>Events</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={handleCreate}>
          Create Event
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={events}
        rowKey="id"
        loading={loading}
      />

      <Modal
        title={editingEvent ? 'Edit Event' : 'Create Event'}
        open={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={700}
      >
        <EventForm
          event={editingEvent}
          onSuccess={handleModalClose}
          onCancel={() => setIsModalVisible(false)}
        />
      </Modal>
    </div>
  );
}
